import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api',
  withCredentials: true,
});

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - Add token to headers
api.interceptors.request.use(
  (config) => {
    // Don't add token to refresh endpoint
    if (config.url?.includes('/auth/refresh')) {
      return config;
    }

    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with queuing and Redux integration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if this is a 401 error with expired token message
    if (
      error.response?.status === 401 &&
      error.response?.data?.message === "Invalid or expired access token" &&
      !originalRequest._retry
    ) {
      // Mark request as retry to prevent infinite loops
      originalRequest._retry = true;

      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        console.log('🔄 Token refresh in progress, queuing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      isRefreshing = true;

      try {
        console.log('🔄 Token expired, attempting refresh...');

        // Call refresh endpoint
        const refreshResponse = await api.post('/auth/refresh');
        const newToken = refreshResponse.data.accessToken;

        if (!newToken) {
          throw new Error('No access token received from refresh');
        }

        // Update localStorage
        localStorage.setItem('token', newToken);

        // Update Redux store
        const store = await import('../store/store');
        const { setToken } = await import('../store/slices/authSlice');
        store.default.dispatch(setToken(newToken));

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Process the queue with the new token
        processQueue(null, newToken);

        console.log('✅ Token refreshed successfully');

        // Retry the original request
        return api(originalRequest);

      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);

        // Process queue with error
        processQueue(refreshError, null);

        // Update Redux store - logout user
        try {
          const store = await import('../store/store');
          const { logout } = await import('../store/slices/authSlice');
          store.default.dispatch(logout());
        } catch (importError) {
          console.error('Failed to import store for logout:', importError);
        }

        // Clear storage
        localStorage.clear();

        // Redirect to login page
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other 403 errors (insufficient permissions)
    if (error.response?.status === 403) {
      console.warn('❌ Access forbidden - insufficient permissions');
      // Don't logout for 403, user might not have permission for specific resource
    }

    // Handle network errors
    if (!error.response) {
      console.error('❌ Network error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;